//
// Based on:
// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
//
// useRepeatBetweenTimeouts
//
// Wait for "delayMs" between each "call" launch; wait for "call" to end
//   before starting the timeout.
//
// Usage:
//
// useRepeatBetweenTimeouts(
//   call,
//   (enabled) ? delayMs : undefined,
// );
//
// Where call is of this type:
//
// const call = (callback) => {
//   // Do stuff...
//
//   // Signal we're done
//   callback();
// }
//

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const useRepeatBetweenTimeouts = (call, delayMs, logLabel = undefined) => {
  const savedCall = useRef(false);
  const [restartNeeded, setRestartNeeded] = useState(true);

  // Remember the latest callback.
  useEffect(
    () => { savedCall.current = call; },
    [call],
  );

  useEffect(
    () => {
      if (logLabel) console.log(`useRepeatBetweenTimeouts ${logLabel} restartNeeded=${restartNeeded}`);
    },
    [logLabel, restartNeeded],
  );
  useEffect(
    () => {
      if (logLabel) console.log(`useRepeatBetweenTimeouts ${logLabel} delayMs=${delayMs}`);
    },
    [logLabel, delayMs],
  );
  useEffect(
    () => {
      if (logLabel) console.log(`useRepeatBetweenTimeouts ${logLabel} call changed`);
    },
    [logLabel, call],
  );

  const callbackEnded = useCallback(
    () => {
      setTimeout(
        () => {
          if (savedCall.current) setRestartNeeded(true);
        },
        delayMs,
      );
    },
    [delayMs],
  );

  useEffect(
    () => {
      if (restartNeeded && delayMs) {
        setRestartNeeded(false);
        savedCall.current(callbackEnded);
      }
    },
    [callbackEnded, restartNeeded, delayMs],
  );
};

export default useRepeatBetweenTimeouts;
